const IMG = {
    chartDark: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1600&q=80",
    goldBars: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1600&q=80",
    monitors: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=1600",
    screens: "https://images.unsplash.com/photo-1642790106117-e829e14a795f?auto=format&fit=crop&w=1600&q=80",
    savings: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=1600&q=80",
    industrial: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1600&q=80",
};

export const BLOGS_A = [
    {
        slug: "what-is-a-forex-trading-bot",
        title: "What Is a Forex Trading Bot? How Algorithmic Trading Really Works",
        excerpt:
            "Behind the buzzword: what an expert advisor actually does on your account, where the genuine edge comes from, and why most retail bots fail within a year.",
        category: "Trading Bots",
        date: "Jun 18, 2026",
        readTime: "7 min read",
        image: IMG.chartDark,
        content: [
            {
                p: [
                    "A forex trading bot — often called an expert advisor, or EA — is a program that monitors market prices and executes trades according to a fixed set of coded rules. It does not predict the future. It applies a defined logic, such as a moving-average crossover filtered by volatility, with the same discipline on the thousandth trade as on the first. Industry estimates suggest that algorithmic systems now account for the majority of volume in institutional currency markets, which is precisely why understanding them matters even if you never run one.",
                ],
            },
            {
                h: "What a bot actually does well",
                p: [
                    "Bots excel at three things humans are poor at: speed, consistency and emotional neutrality. They react in milliseconds, never widen a stop-loss out of hope, and never double a position out of frustration. On instruments like XAU/USD — where a London-session breakout can travel fifty dollars in minutes — that execution discipline is the product.",
                    "A well-built bot also enforces risk mechanically. Position size is computed from account equity and stop distance before every order, so a string of losses degrades the account gracefully instead of catastrophically.",
                ],
            },
            {
                h: "What a bot cannot do",
                p: [
                    "No algorithm is immune to regime change. A trend-following system tuned on 2024's trending gold market can bleed steadily through a 2026 range. Bots also inherit every assumption their developer made about spreads, slippage and broker execution. This is why 'set and forget' marketing should be treated as a red flag: serious operators monitor their systems daily and retire strategies when the underlying edge decays.",
                ],
            },
            {
                h: "How we evaluate a bot before release",
                p: [
                    "At Pocketfolio, a strategy must survive four gates before it reaches a client: a multi-year backtest across at least two distinct market regimes, walk-forward validation on unseen data, a minimum incubation period on a live account, and hard-coded drawdown limits that disable the system automatically. Roughly nine in ten candidate strategies never make it past the second gate — and that rejection rate is the point.",
                ],
            },
        ],
    },
    {
        slug: "gold-xauusd-commodity-trading",
        title: "Gold in 2026: Why XAU/USD Remains the Centre of Commodity Trading",
        excerpt:
            "Central banks are buying at a historic pace, real yields are softening, and every dip gets absorbed. A practical framework for trading the world's oldest asset.",
        category: "Commodities",
        date: "Jun 04, 2026",
        readTime: "6 min read",
        image: IMG.goldBars,
        content: [
            {
                p: [
                    "Gold has occupied the centre of the commodity complex for five thousand years, and 2026 has not changed that. What has changed is the buyer base. According to World Gold Council data, central banks have purchased gold at a record pace since 2022 — consistently above one thousand tonnes per year — led by emerging-market reserve managers diversifying away from single-currency exposure. That bid is structural, not speculative, and it has changed how dips behave: they get bought, fast.",
                ],
            },
            {
                h: "The three forces that move XAU/USD",
                p: [
                    "First, real yields. Gold pays no coupon, so its opportunity cost is the inflation-adjusted return on US Treasuries; when real yields fall, gold historically rises. Second, the dollar. Because gold is priced in USD, a weaker DXY mechanically lifts the metal — the correlation is negative in most regimes. Third, fear. Geopolitical shocks and banking stress send capital into gold within hours, often before equities have finished gap-adjusting.",
                    "For traders, the practical takeaway is that gold trends hardest when all three align — falling real yields, a soft dollar, and elevated risk stress. When they conflict, gold ranges, and range tactics outperform.",
                ],
            },
            {
                h: "Trading gold with discipline",
                p: [
                    "Gold is not a forgiving instrument. Spreads widen around CPI and FOMC releases, and fifty-dollar intraday swings can liquidate an oversized position in minutes. Our desks treat event windows as no-trade zones, size positions from a fixed fraction of equity, and respect the round numbers — 2,300, 2,400, 2,500 — which act as genuine liquidity shelves rather than superstition.",
                    "This is also why gold is our flagship bot market: its deep liquidity, clean technical structure and 23-hour trading day make it unusually well suited to rules-based execution.",
                ],
            },
        ],
    },
    {
        slug: "forex-signals-explained",
        title: "Forex Signals Explained: How to Read and Use Them Responsibly",
        excerpt:
            "A signal is a hypothesis with risk parameters attached — not a tip. Here is the anatomy of a professional signal and the habits that separate users from gamblers.",
        category: "Signals",
        date: "May 21, 2026",
        readTime: "6 min read",
        image: IMG.monitors,
        content: [
            {
                p: [
                    "A forex signal is a trade recommendation: instrument, direction, entry, stop-loss and take-profit. Used well, signals are a way to rent a research process you do not have time to run yourself. Used badly — copied blindly with random sizing — they are simply outsourced gambling. The difference is entirely in how you consume them.",
                ],
            },
            {
                h: "The anatomy of a professional signal",
                p: [
                    "Every signal worth following contains five elements: the instrument, the direction, a defined entry zone, an invalidation point (the stop-loss), and at least one profit target. From those five numbers you can compute the only metric that matters before entry — the reward-to-risk ratio. A signal with no stop-loss is not a signal; it is an opinion.",
                    "Professional desks also attach context: the structure that triggered the idea, the event risk on the calendar, and the expected holding period. Context lets you judge whether the setup still applies if you see the signal an hour late.",
                ],
            },
            {
                h: "What realistic performance looks like",
                p: [
                    "Be suspicious of any provider advertising 90% win rates. Sustainable strategies typically win 40–60% of the time and make their money through asymmetric payoffs — risking one unit to make two or three. A provider who reports maximum drawdown alongside returns is telling you they understand the mathematics of survival; one who only shows screenshots of winners is marketing, not trading.",
                ],
            },
            {
                h: "A responsible user's checklist",
                p: [
                    "Fix your risk per signal before you subscribe — typically 0.5–1% of account equity. Journal every signal you take, including the ones you skip. Never 'average down' a losing signal, and never let one provider represent your entire exposure. Signals are one input into a process. The process is still yours.",
                ],
            },
        ],
    },
    {
        slug: "etfs-for-forex-traders",
        title: "ETFs for Forex Traders: Diversifying Beyond Currency Pairs",
        excerpt:
            "SPY, QQQ, GLD, USO — exchange-traded funds give currency traders a second lens on the same macro story, with cleaner structure and deeper liquidity than most crosses.",
        category: "ETFs",
        date: "May 07, 2026",
        readTime: "6 min read",
        image: IMG.screens,
        content: [
            {
                p: [
                    "Most retail forex traders live entirely inside currency pairs, yet the same macro forces that move EUR/USD also move the S&P 500, gold and crude oil — often with cleaner technical structure. Exchange-traded funds package those markets into instruments that trade like a single stock, and they deserve a place in every macro trader's toolkit.",
                ],
            },
            {
                h: "Why ETFs complement a forex book",
                p: [
                    "Consider the gold trade. A trader long XAU/USD is exposed to the metal and to short-term dollar noise. The same view expressed through GLD — the largest physically backed gold ETF — tracks the same underlying with defined exchange hours and deep options liquidity for hedging. Oil traders face a similar choice between spot WTI and USO, the crude oil ETF. The instruments rhyme, but their microstructure differs, and those differences create both hedges and opportunities.",
                    "Index ETFs add another dimension. SPY and QQQ are effectively real-time polls of global risk appetite. A forex trader watching QQQ break down through a weekly level receives an early warning for risk-sensitive currencies like AUD and NZD before the move shows up in the crosses.",
                ],
            },
            {
                h: "Structure, gaps and sizing",
                p: [
                    "ETFs trade on exchange hours, which means overnight gap risk is real — positions need to be sized so a 3% opening gap is an inconvenience, not an account event. In exchange, you get transparency: published holdings, regulated custody and no counterparty spread games.",
                    "Our signals desk uses ETFs two ways: as tradeable instruments in their own right, and as confirmation filters for commodity and index trades. When GLD, spot gold and gold miners all break out together, the probability of follow-through is materially higher than when gold moves alone.",
                ],
            },
        ],
    },
    {
        slug: "risk-management-algorithmic-trading",
        title: "Risk Management Rules Every Algorithmic Trader Should Follow",
        excerpt:
            "Automation magnifies whatever you give it — including mistakes. Five non-negotiable rules that keep a bot-enhanced account alive through bad regimes.",
        category: "Education",
        date: "Apr 23, 2026",
        readTime: "7 min read",
        image: IMG.savings,
        content: [
            {
                p: [
                    "An algorithm removes emotional execution errors, but it introduces a new failure mode: it will execute a bad idea perfectly, at machine speed, until the account is empty. Risk management for automated trading is therefore not a feature — it is the architecture. These are the five rules our own systems are built around.",
                ],
            },
            {
                h: "Rule 1 — Risk a fixed fraction, never a fixed lot",
                p: [
                    "Position size must be computed from current equity and stop distance, so that every trade risks the same percentage — typically 0.5–1%. Fixed-lot traders unknowingly increase their risk as the account shrinks, accelerating drawdowns exactly when they can least afford them.",
                ],
            },
            {
                h: "Rule 2 — Install a kill switch",
                p: [
                    "Every bot needs an equity stop: a hard daily or weekly drawdown level at which the system closes positions and disables itself until a human reviews it. Markets occasionally go non-stationary — flash crashes, surprise interventions, broken liquidity. The kill switch is the difference between a bad week and a funeral.",
                ],
            },
            {
                h: "Rule 3 — Cap correlated exposure",
                p: [
                    "Three bots trading EUR/USD, GBP/USD and gold are often one bet on the dollar wearing three costumes. Correlation-capped portfolios limit total exposure per theme, so a single macro shock cannot hit every strategy simultaneously.",
                ],
            },
            {
                h: "Rule 4 — Trust live data over backtests",
                p: [
                    "Backtests assume perfect fills. Live markets slip, spread and gap. Track the delta between backtested and realised performance; when slippage consistently exceeds assumptions, the strategy's true expectancy may already be negative.",
                ],
            },
            {
                h: "Rule 5 — Retire strategies on evidence, not on hope",
                p: [
                    "Define in advance what failure looks like — for example, a drawdown exceeding 1.5 times the historical maximum, or a rolling win rate outside the tested confidence band. When the evidence says the edge is gone, the correct trade is the off switch.",
                ],
            },
        ],
    },
    {
        slug: "crude-oil-wti-brent-trading",
        title: "Crude Oil Trading: What Actually Moves WTI and Brent",
        excerpt:
            "Inventories, OPEC+ arithmetic, the dollar, and the futures curve — a field guide to the world's most political commodity and its two great benchmarks.",
        category: "Commodities",
        date: "Apr 09, 2026",
        readTime: "6 min read",
        image: IMG.industrial,
        content: [
            {
                p: [
                    "Oil is the only commodity whose price is partly decided around a conference table. That makes it both the most macro-driven and the most headline-sensitive market a retail trader can touch. Before trading it, you need to know which oil you are trading: WTI, the US benchmark settled at Cushing, Oklahoma, and Brent, the seaborne North Sea benchmark that prices most of the world's physical crude.",
                ],
            },
            {
                h: "The four drivers that matter",
                p: [
                    "Supply arithmetic comes first: OPEC+ production quotas, US shale output and strategic reserve releases set the floor and ceiling for the medium-term trend. Second, inventories — the weekly EIA report is oil's scheduled volatility event, and experienced traders treat it the way FX traders treat payrolls. Third, the dollar: crude is priced in USD, so a strong dollar quietly tightens global oil affordability. Fourth, geopolitics, which can add or remove a five-dollar risk premium overnight.",
                ],
            },
            {
                h: "Read the curve, not just the price",
                p: [
                    "The shape of the futures curve tells you what the spot price cannot. Backwardation — near contracts above later ones — signals tight physical supply and rewards longs. Contango — near contracts below later ones — signals glut and quietly taxes long positions through negative roll yield. Many losing oil trades were right on direction and wrong on the curve.",
                ],
            },
            {
                h: "Sizing for oil's personality",
                p: [
                    "Crude routinely moves 2–3% in a session and can gap violently on weekend headlines. Our bots trade it with roughly half the position size they would use on gold for the same stop distance, stand aside during EIA minutes, and never hold full exposure through OPEC+ meeting windows. In oil, respect for event risk is not caution — it is edge preservation.",
                ],
            },
        ],
    },
];
